import React, { useState, useEffect } from "react";
import { Eye, TrendingUp, DollarSign } from "lucide-react";
import ProductDetailsView from "./ProductDetailsView";
import { DataContractDetailsProduct } from "../DataTrafficLights/DataContractDetailsProduct";
import heartLoading from "../../../assests/heartLoading.gif";
import { Tooltip } from "@mui/material";
import axios from "axios";

const ProductModal = ({
  showProductModal,
  selectedProductForView,
  productLoading,
  setShowProductModal,
  setSelectedProductForView,
}) => {
  const [activeTab, setActiveTab] = useState("view");

  // Traffic light state
  const [trafficContract, setTrafficContract] = useState(null);
  const [trafficLoading, setTrafficLoading] = useState(false);
  const [trafficError, setTrafficError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [consumerData, setConsumerData] = useState(null);

  // Fetch contract data when traffic tab becomes active
  useEffect(() => {
    if (
      activeTab === "traffic" &&
      selectedProductForView?.productId &&
      !trafficContract
    ) {
      fetchTrafficContract(selectedProductForView.productId);
    }
  }, [activeTab, selectedProductForView]);

  // Reset traffic data when modal closes
  useEffect(() => {
    if (!showProductModal) {
      setTrafficContract(null);
      setTrafficError(null);
      setConsumerData(null);
      setActiveTab("view");
    }
  }, [showProductModal]);

  // Reset traffic data when product changes
  useEffect(() => {
    setTrafficContract(null);
    setTrafficError(null);
    setConsumerData(null);
  }, [selectedProductForView?.productId]);

  const handleCopyLink = async (event) => {
    if (event && typeof event.stopPropagation === "function") event.stopPropagation();
    try {
      const linkToCopy = `${window.location.origin}/data-traffic-lights?viewProduct=${trafficContract?.name}`;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(linkToCopy);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = linkToCopy;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopiedId(trafficContract?.name);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error("Failed to copy link", error);
    }
  };

  const fetchTrafficContract = async (productId) => {
    try {
      setTrafficLoading(true);
      setTrafficError(null);

      // Step 1: fetch contract first to get contract.id
      const contractResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-data-contract/${productId}`
      );

      if (!contractResponse.data) {
        setTrafficError("No contract data found.");
        return;
      }

      // Step 2: fetch consumer dependencies using contract.id (same as DataContractDetails does)
      const consumerResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-consumer-dependencies/${contractResponse.data.id}`
      );

      // Both done — set both at once — ONE loader total
      setTrafficContract(contractResponse.data);
      setConsumerData(consumerResponse.data);

    } catch (err) {
      console.error("Failed to fetch traffic contract:", err);
      setTrafficError("Failed to load traffic light data. Please try again.");
    } finally {
      setTrafficLoading(false);
    }
  };

  if (!showProductModal || !selectedProductForView) return null;

  const tabs = [
    { id: "view", label: "View Product", icon: Eye },
    { id: "traffic", label: "Traffic Light", icon: TrendingUp },
    { id: "cost", label: "Cost", icon: DollarSign },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl w-[95%] max-w-[1400px] max-h-[90vh] overflow-hidden">

        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Product Details</h2>
          </div>
          <button
            onClick={() => {
              setShowProductModal(false);
              setSelectedProductForView(null);
            }}
            className="text-black p-2"
            title="Close"
          >
            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex ml-[20px]">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 px-5 py-3 font-medium text-sm transition-colors whitespace-nowrap
                    ${isActive
                      ? "text-[#000080] border-b-2 border-[#000080] bg-white"
                      : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                    }
                  `}
                >
                  <Icon className="h-5 w-5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Modal Body */}
        <div className="overflow-y-auto max-h-[calc(90vh-130px)]">
          {productLoading ? (
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="flex flex-col items-center space-y-3">
                <img src={heartLoading} alt="Loading..." className="h-30" />
                <span className="text-gray-700 text-[20px] text-center">
                  Loading product details...
                </span>
              </div>
            </div>
          ) : selectedProductForView ? (
            <>
              {/* ── View Product Tab ── */}
              {activeTab === "view" && (
                <div className="p-2">
                  <ProductDetailsView product={selectedProductForView} />
                </div>
              )}

              {/* ── Traffic Light Tab ── */}
              {activeTab === "traffic" && (
                <>
                  {/* ONE loader for both APIs */}
                  {trafficLoading && (
                    <div className="flex items-center justify-center min-h-[60vh]">
                      <div className="flex flex-col items-center space-y-3">
                        <img src={heartLoading} alt="Loading..." className="h-30" />
                        <span className="text-gray-700 text-[20px] text-center">
                          Loading traffic light data...
                        </span>
                      </div>
                    </div>
                  )}

                  {!trafficLoading && trafficError && (
                    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
                      <TrendingUp className="w-16 h-16 text-gray-300" />
                      <p className="text-gray-500">{trafficError}</p>
                      <button
                        onClick={() => fetchTrafficContract(selectedProductForView.productId)}
                        className="px-4 py-2 bg-[#bb1ccc] text-white rounded-lg text-sm hover:bg-blue-800 transition-colors"
                      >
                        Retry
                      </button>
                    </div>
                  )}

                  {!trafficLoading && !trafficError && trafficContract && (
                    <div className="px-6">
                      {/* Sub-header: product name + copy link */}
                      <div className="flex items-center gap-4 px-4 py-3 border-b border-gray-100 bg-gray-50">
                        <h3 className="text-xl font-bold text-gray-900">
                          {trafficContract.name}
                        </h3>
                        <Tooltip
                          title={copiedId === trafficContract.name ? "Copied!" : "Copy direct link"}
                          arrow
                          placement="right"
                          PopperProps={{ sx: { zIndex: 100000000 } }}
                        >
                          <span className="inline-flex">
                            <button
                              onClick={handleCopyLink}
                              style={{
                                fontSize: "12px",
                                whiteSpace: "nowrap",
                                display: "inline-flex",
                                backgroundColor: copiedId === trafficContract.name ? "#15803d" : "#1D7285",
                                color: "white",
                                padding: "5px 8px",
                                borderRadius: "3px",
                                border: "none",
                                cursor: "pointer",
                                transition: "background-color 0.2s",
                              }}
                            >
                              {copiedId === trafficContract.name ? "✓ Copied" : "Copy Link"}
                            </button>
                          </span>
                        </Tooltip>
                      </div>

                      {/* Pass prefetchedConsumerData so no second loader */}
                      <DataContractDetailsProduct
                        contract={trafficContract}
                        prefetchedConsumerData={consumerData}
                      />
                    </div>
                  )}

                  {!trafficLoading && !trafficError && !trafficContract && (
                    <div className="flex flex-col items-center justify-center min-h-[60vh]">
                      <TrendingUp className="w-16 h-16 text-gray-300 mb-4" />
                      <p className="text-gray-500">No traffic light data available for this product.</p>
                    </div>
                  )}
                </>
              )}

              {/* ── Cost Tab ── */}
              {activeTab === "cost" && (
                <div className="p-6 h-[60vh] flex flex-col items-center justify-center text-center">
                  <DollarSign className="w-16 text-gray-400 mb-4" />
                  <p className="text-gray-600">Cost analysis content coming soon...</p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600">No product details available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductModal;